import type { ExternalTestProvider } from '@chatsdet/types';

/**
 * The canonical external-test identity.
 *
 * @module test-reporters/test-key
 * @description A `testKey` is the stable name of one test across attempts, runs
 * and producers: `{repo}/{specPath}#{suite › … › title}`. It is the join column
 * between everything in this feature — the ingest parser derives it from the
 * framework's report, the boundary hook derives it from the live mocha runner,
 * and the repo resolves it to an `external_test_items` row so per-test capture
 * can be attached.
 *
 * It lives in its own module for exactly that reason: two builders that agree
 * today and drift tomorrow would silently detach every video from its test, and
 * the failure would look like "capture didn't work" rather than "the key
 * changed". One function, one separator, both callers.
 */

/**
 * Separator between suite titles in a key's title path. A U+203A single right
 * angle quote, matching what mochawesome and the UI already render.
 */
export const TITLE_SEPARATOR = ' › ';

export interface BuildTestKeyArgs {
  /** `owner/repo` slug; omitted for a bare local run. */
  repo?: string | null;
  /** Spec file path relative to the repo root. */
  specPath: string;
  /** Suite titles from outermost to innermost, ending with the test title. */
  titlePath: string[];
}

/** Build the canonical `{repo}/{spec}#{a › b › c}` identity for one test. */
export function buildTestKey({ repo, specPath, titlePath }: BuildTestKeyArgs): string {
  const base = repo ? `${repo}/${specPath}` : specPath;
  return `${base}#${titlePath.join(TITLE_SEPARATOR)}`;
}

/** JIRA-style ticket key (WEB-123, ABC-42) — the natural scenario boundary. */
export const TICKET_RE = /\b[A-Z][A-Z0-9]+-\d+\b/;

/** Separator between the spec file and the scenario label in a scenario key. */
const SCENARIO_KEY_SEPARATOR = '␟';

export interface ParsedScenario {
  /** Repo-qualified spec file (the part before `#`). */
  specFile: string;
  /** Human label — the ticketed `describe`, e.g. `WEB-31415 — Invoice Page`. */
  label: string;
  /** Stable identity for grouping. Spec-qualified so same-named blocks stay distinct. */
  key: string;
}

/**
 * The scenario ("test", in customer terms) a testKey belongs to.
 *
 * A ticketed `describe` block is the unit a customer reasons about: they file
 * WEB-31415, not its many `it()` steps. That makes it the unit the run list charts
 * AND the unit a shard boundary must respect — splitting mid-scenario would put
 * a ticket's setup in one container and its assertions in another.
 *
 * Lives here, beside {@link buildTestKey}, for the same reason that does: the UI
 * grouping and the shard partition deriving this differently would mean the
 * boundaries a reviewer sees are not the boundaries that ran.
 */
export function parseScenario(testKey: string, displayName?: string): ParsedScenario {
  const hashIdx = testKey.indexOf('#');
  const specFile = hashIdx >= 0 ? testKey.slice(0, hashIdx) : '';
  const afterHash = hashIdx >= 0 ? testKey.slice(hashIdx + 1) : (displayName ?? testKey);
  const path = afterHash
    .split(TITLE_SEPARATOR)
    .map((s) => s.trim())
    .filter(Boolean);
  // The ticketed describe if present, else the 2nd describe level (generic
  // suites), else the top level.
  const label = path.find((s) => TICKET_RE.test(s)) ?? path[1] ?? path[0] ?? 'Scenario';
  return { specFile, label, key: `${specFile}${SCENARIO_KEY_SEPARATOR}${label}` };
}

/**
 * Resolve the customer-visible test boundary for a provider.
 *
 * Selenium/Mocha suites commonly encode one ticketed scenario as several
 * framework tests, so they retain the historical scenario grouping above.
 * Playwright already reports a complete leaf test with its own native step
 * timeline; grouping several leaves together would incorrectly present those
 * tests as steps. Keep each Playwright test distinct and use its immediate
 * authored group as title context (for example `Checkout > submits card`).
 */
export function parseProviderScenario(
  provider: ExternalTestProvider,
  testKey: string,
  displayName: string
): ParsedScenario {
  if (provider !== 'playwright') return parseScenario(testKey, displayName);

  const hashIdx = testKey.indexOf('#');
  const specFile = hashIdx >= 0 ? testKey.slice(0, hashIdx) : '';
  const afterHash = hashIdx >= 0 ? testKey.slice(hashIdx + 1) : displayName;
  const path = afterHash
    .split(TITLE_SEPARATOR)
    .map((segment) => segment.trim())
    .filter(Boolean);
  const leaf = path.at(-1) ?? displayName;
  const group = path.at(-2);
  const label = group ? `${group} > ${leaf}` : leaf;
  return { specFile, label, key: `${specFile}${SCENARIO_KEY_SEPARATOR}${afterHash}` };
}
